import { ReleaseNote, ReleaseNotesState } from '@/types/releaseNotes'

// Import release notes from generated JSON file
// This file is generated by `npm run release-notes:build` from markdown files
// Using synchronous import - the file will be included in the bundle during build
import releaseNotesData from './release-notes-data.json'

const STORAGE_KEY = 'datacentral_release_notes'

// Fallback - empty array since JSON should always be generated during build
const fallbackReleaseNotes: ReleaseNote[] = []

// Cast imported JSON data to ReleaseNote type
const typedReleaseNotesData = releaseNotesData as ReleaseNote[]

// Export merged release notes (generated JSON + fallback)
export const mockReleaseNotes: ReleaseNote[] =
    typedReleaseNotesData.length > 0
        ? typedReleaseNotesData
        : fallbackReleaseNotes

// Helper to get all release notes
const getAllNotes = (): ReleaseNote[] => {
    return typedReleaseNotesData.length > 0
        ? typedReleaseNotesData
        : fallbackReleaseNotes
}

/**
 * Clear invalid read notes that don't exist in current release notes
 * This prevents stale data from causing incorrect unread counts
 */
const cleanupInvalidReadNotes = (
    state: ReleaseNotesState
): ReleaseNotesState => {
    const validIds = new Set(getAllNotes().map((note) => note.id))
    const cleanedReadNotes = state.readNotes.filter((id) => validIds.has(id))

    // Only update if something was cleaned
    if (cleanedReadNotes.length !== state.readNotes.length) {
        return { ...state, readNotes: cleanedReadNotes }
    }
    return state
}

export const getReleaseNotesState = (): ReleaseNotesState => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            const state = JSON.parse(stored) as ReleaseNotesState
            // Auto-cleanup invalid IDs
            const cleanedState = cleanupInvalidReadNotes(state)
            if (cleanedState !== state) {
                saveReleaseNotesState(cleanedState)
            }
            return cleanedState
        }
    } catch (error) {
        console.error('Error reading release notes state:', error)
    }

    return {
        readNotes: [],
        lastChecked: new Date().toISOString(),
    }
}

/**
 * Force clear all release notes state (for debugging/reset)
 */
export const clearReleaseNotesState = (): void => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(LAST_SEEN_VERSION_KEY)
}

export const saveReleaseNotesState = (state: ReleaseNotesState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (error) {
        console.error('Error saving release notes state:', error)
    }
}

export const markNoteAsRead = (noteId: string): void => {
    const state = getReleaseNotesState()
    if (!state.readNotes.includes(noteId)) {
        state.readNotes.push(noteId)
        saveReleaseNotesState(state)
    }
}

export const getUnreadNotes = (): ReleaseNote[] => {
    const state = getReleaseNotesState()
    return getAllNotes().filter((note) => !state.readNotes.includes(note.id))
}

export const getAllReleaseNotes = (): ReleaseNote[] => {
    return getAllNotes().sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
}

export const updateLastChecked = (): void => {
    const state = getReleaseNotesState()
    state.lastChecked = new Date().toISOString()
    saveReleaseNotesState(state)
}

/**
 * Get current app version from build
 */
export const getCurrentAppVersion = (): string => {
    return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'
}

/**
 * Get the last seen app version from localStorage
 */
const LAST_SEEN_VERSION_KEY = 'datacentral_last_seen_version'

export const getLastSeenVersion = (): string | null => {
    return localStorage.getItem(LAST_SEEN_VERSION_KEY)
}

export const setLastSeenVersion = (version: string): void => {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, version)
}

/**
 * Compare two semver versions
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export const compareVersions = (v1: string, v2: string): number => {
    const normalize = (v: string) => v.replace(/^v/, '').split('.').map(Number)
    const parts1 = normalize(v1)
    const parts2 = normalize(v2)

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const p1 = parts1[i] || 0
        const p2 = parts2[i] || 0
        if (p1 > p2) return 1
        if (p1 < p2) return -1
    }
    return 0
}

/**
 * Check if there's a new version since last seen
 */
export const hasNewVersion = (): boolean => {
    const currentVersion = getCurrentAppVersion()
    const lastSeenVersion = getLastSeenVersion()

    if (!lastSeenVersion) {
        // First time user - don't show modal, just save current version
        setLastSeenVersion(currentVersion)
        return false
    }

    return compareVersions(currentVersion, lastSeenVersion) > 0
}

/**
 * Get release notes for versions newer than last seen
 */
export const getNewVersionNotes = (): ReleaseNote[] => {
    const lastSeenVersion = getLastSeenVersion()

    if (!lastSeenVersion) {
        return []
    }

    return getAllNotes().filter((note) => {
        return compareVersions(note.version, lastSeenVersion) > 0
    })
}

/**
 * Mark current version as seen
 */
export const markCurrentVersionAsSeen = (): void => {
    const currentVersion = getCurrentAppVersion()
    setLastSeenVersion(currentVersion)
}
